import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  fetchIdeas,
  fetchRuns,
  fetchTopic,
  fmtRelative,
  triggerRun,
} from "../lib/api";
import IdeaRow from "../components/IdeaRow";
import UserLibraryWidget from "../components/UserLibraryWidget";
import HowItWorks from "../components/HowItWorks";
import { toast } from "sonner";
import { Zap } from "lucide-react";

export default function TopicPage() {
  const { slug } = useParams();
  const qc = useQueryClient();
  const [status, setStatus] = useState("active");
  const [sort, setSort] = useState("idea_rank");
  const [busy, setBusy] = useState(false);

  const { data: topic } = useQuery({
    queryKey: ["topic", slug],
    queryFn: () => fetchTopic(slug),
    refetchInterval: 10000,
  });
  const { data: ideas = [] } = useQuery({
    queryKey: ["ideas", slug, status, sort],
    queryFn: () => fetchIdeas({ topic_slug: slug, status, sort, limit: 200 }),
    refetchInterval: 10000,
  });
  const { data: runs = [] } = useQuery({
    queryKey: ["runs", slug],
    queryFn: () => fetchRuns({ topic_slug: slug, limit: 8 }),
    refetchInterval: 10000,
  });

  const trigger = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await triggerRun({ topic_slug: slug, target_count: 10 });
      toast.success(`INGESTION QUEUED · ${slug.toUpperCase()}`);
      setTimeout(() => qc.invalidateQueries({ queryKey: ["topic", slug] }), 1500);
    } catch (e) {
      toast.error("RUN FAILED", { description: e?.message || "" });
    } finally {
      setBusy(false);
    }
  };

  if (!topic) {
    return (
      <div className="p-6 text-zinc-500 font-mono text-[12px]">LOADING_TOPIC…</div>
    );
  }
  const filled = topic.today_count >= topic.today_target;
  const pct = Math.min(100, (topic.today_count / topic.today_target) * 100);

  return (
    <div data-testid="topic-page" className="p-4 space-y-4">
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
            TOPIC_LIBRARY
          </div>
          <h1 className="font-mono text-2xl tracking-tight mt-1">
            {topic.name}
          </h1>
          <p className="text-zinc-500 text-[12px] mt-1 max-w-2xl">
            {topic.description}
          </p>
        </div>

        <div className="ml-auto flex items-end gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
              TODAY
            </div>
            <div
              className={
                "text-3xl font-mono tabnum " +
                (filled ? "text-emerald-400" : "text-amber-400")
              }
            >
              {topic.today_count}
              <span className="text-zinc-500 text-base ml-1">
                / {topic.today_target}
              </span>
            </div>
            <div className="mt-1 h-[3px] w-40 bg-[#1a1a1a]">
              <div
                style={{
                  width: `${pct}%`,
                  height: "100%",
                  background: filled ? "#10B981" : "#F59E0B",
                }}
              />
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
              ACTIVE_TOTAL
            </div>
            <div className="text-3xl font-mono tabnum">{topic.total_active}</div>
            <div className="text-[10px] text-zinc-500 font-mono mt-1">
              last_run {fmtRelative(topic.last_run_at)}
            </div>
          </div>
          <button
            onClick={trigger}
            disabled={busy}
            data-testid="topic-run-button"
            className="h-9 px-3 text-[11px] font-mono uppercase tracking-wider border border-white bg-white text-black hover:bg-zinc-200 disabled:opacity-50 flex items-center gap-2"
          >
            <Zap size={12} /> {busy ? "QUEUEING…" : "TOP_UP"}
          </button>
        </div>
      </div>

      <HowItWorks
        id="topic"
        tag="10/DAY · 3 SOURCES · 30-DAY ROLLING"
        headline={`Your daily feed of verified ideas for ${topic.name}. Every item ships with three independent ranked references — saveable forever.`}
        steps={[
          { accent: "emerald", label: "Read fresh ideas", body: "10 new items are ingested each day. Today's progress bar above shows how many landed so far." },
          { accent: "amber", label: "Open the sources", body: "Each idea carries three independent refs (S1 + S2 + S3) — ranked by credibility × recency × repeat-value. Older items may show 2/3 until re-sourced." },
          { accent: "purple", label: "Filter + sort", body: "Use STATUS (active / archived / expired / all) and SORT (rank, newest, oldest) to slice the feed." },
          { accent: "sky", label: "Save to MY_LIB", body: "Hit the bookmark icon on any row. Saves persist even after the original expires on day 31." },
          { accent: "rose", label: "Top up manually", body: "Below quota? TOP_UP triggers an immediate ingestion run to refill toward the 10/day target." },
        ]}
      />

      {/* user library widget for this topic */}
      <UserLibraryWidget topicSlug={slug} topicName={topic.name} />

      {/* filters */}
      <div className="flex items-center gap-2 border-y border-[#27272A] px-3 py-2 bg-[#0E0E0E]">
        <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
          STATUS
        </span>
        {["active", "archived", "expired", "all"].map((s) => (
          <button
            key={s}
            data-testid={`filter-status-${s}`}
            onClick={() => setStatus(s)}
            className={
              "h-7 px-2 text-[10px] font-mono uppercase tracking-wider border " +
              (status === s
                ? "border-white bg-white text-black"
                : "border-[#27272A] text-zinc-400 hover:text-white hover:bg-[#141414]")
            }
          >
            {s}
          </button>
        ))}
        <span className="ml-6 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
          SORT
        </span>
        {[
          { v: "idea_rank", l: "IDEA_RANK" },
          { v: "source_rank", l: "SRC_RANK" },
          { v: "created_at", l: "NEWEST" },
        ].map((s) => (
          <button
            key={s.v}
            data-testid={`filter-sort-${s.v}`}
            onClick={() => setSort(s.v)}
            className={
              "h-7 px-2 text-[10px] font-mono uppercase tracking-wider border " +
              (sort === s.v
                ? "border-white bg-white text-black"
                : "border-[#27272A] text-zinc-400 hover:text-white hover:bg-[#141414]")
            }
          >
            {s.l}
          </button>
        ))}
        <span className="ml-auto text-[10px] font-mono text-zinc-500 tabnum">
          {ideas.length} ROWS
        </span>
      </div>

      {/* ideas */}
      <section className="border border-[#27272A] bg-[#0A0A0A]">
        {ideas.length === 0 ? (
          <div className="grid-bg p-10 text-center text-[12px] text-zinc-500 font-mono">
            NO_IDEAS_FOR_FILTER
          </div>
        ) : (
          ideas.map((it, i) => <IdeaRow key={it.id} idea={it} index={i} />)
        )}
      </section>

      {/* recent runs for this topic */}
      <section className="border border-[#27272A]">
        <div className="px-3 py-2 border-b border-[#27272A] text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">
          RECENT_RUNS · {slug}
        </div>
        <table className="w-full text-[11px] font-mono">
          <thead className="text-zinc-500">
            <tr className="border-b border-[#161616]">
              <th className="text-left p-2">STARTED</th>
              <th className="text-left p-2">STATUS</th>
              <th className="text-right p-2">INSERTED</th>
              <th className="text-right p-2">DEDUPED</th>
              <th className="text-right p-2">SRC_VERIF</th>
              <th className="text-right p-2">DURATION</th>
              <th className="text-left p-2">TRIGGER</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id} className="border-b border-[#0E0E0E] hover:bg-[#0E0E0E]">
                <td className="p-2">{fmtRelative(r.started_at)}</td>
                <td className="p-2 uppercase">
                  <span
                    className="dot"
                    style={{
                      background:
                        r.status === "completed"
                          ? "#10B981"
                          : r.status === "partial"
                          ? "#F59E0B"
                          : r.status === "failed"
                          ? "#EF4444"
                          : "#3B82F6",
                    }}
                  />
                  {r.status}
                </td>
                <td className="p-2 text-right tabnum">{r.ideas_inserted}</td>
                <td className="p-2 text-right tabnum">{r.ideas_deduped}</td>
                <td className="p-2 text-right tabnum">
                  {r.sources_verified}/{r.sources_attached}
                </td>
                <td className="p-2 text-right tabnum">{r.duration_ms}ms</td>
                <td className="p-2 uppercase text-zinc-500">{r.triggered_by}</td>
              </tr>
            ))}
            {runs.length === 0 && (
              <tr>
                <td colSpan={7} className="p-4 text-center text-zinc-500">
                  NO_RUNS_YET
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
